import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth, useUser, USE_CLERK } from "@/lib/clerk";

const CONSENT_KEY = "spotcar-analytics-consent";
const PROJECT_KEY = process.env.REACT_APP_POSTHOG_KEY || "";
const API_HOST = process.env.REACT_APP_POSTHOG_HOST || "";
const DEVELOPMENT_ENABLED = process.env.REACT_APP_POSTHOG_ENABLE_DEVELOPMENT === "true";
const ALLOWED_HOSTS = new Set(["https://eu.i.posthog.com", "https://us.i.posthog.com"]);
const IS_AVAILABLE = Boolean(
    PROJECT_KEY
    && ALLOWED_HOSTS.has(API_HOST)
    && (process.env.NODE_ENV === "production" || DEVELOPMENT_ENABLED),
);

const AnalyticsConsentContext = React.createContext(null);
let posthogInstance = null;
let posthogPromise = null;
let initialized = false;
let lastPageviewKey = null;
let captureAllowed = false;
let lifecycleVersion = 0;
let selectedIdentity;

function routeTemplate(pathname) {
    if (pathname === "/") return "/";
    if (["/privacy", "/terms"].includes(pathname)) return pathname;
    if (/^\/app\/analysis\/[^/]+$/.test(pathname)) return "/app/analysis/:id";
    if (pathname === "/app") return "/app";
    if (/^\/app\/(dashboard|new|compare|collections|bulk|credits|settings)$/.test(pathname)) {
        return pathname;
    }
    return null;
}

function isReplayRoute(pathname) {
    return routeTemplate(pathname) !== null;
}

function sanitizeUrl(value) {
    if (!value) return undefined;
    try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return undefined;
        const template = routeTemplate(url.pathname);
        if (template) return template;
        return url.pathname.startsWith("/static/") ? url.pathname : undefined;
    } catch {
        return undefined;
    }
}

function maskReplayRequest(data) {
    const name = sanitizeUrl(data.name || data.url);
    if (!name) return null;
    return { ...data, name, url: name, requestHeaders: {}, responseHeaders: {}, requestBody: null, responseBody: null };
}

function maskAttribute(name, value) {
    const normalized = name.toLowerCase();
    if (["href", "src", "action", "formaction", "poster"].includes(normalized)) {
        return sanitizeUrl(value) || "";
    }
    if (
        normalized === "value"
        || normalized === "id"
        || normalized === "title"
        || normalized === "alt"
        || normalized.startsWith("aria-")
        || normalized.startsWith("data-")
    ) {
        return "[redacted]";
    }
    return value;
}

function beforeSend(event) {
    if (!captureAllowed) return null;
    const template = routeTemplate(window.location.pathname);
    if (!template) return null;
    const sanitizeProperties = (properties) => {
        if (!properties || typeof properties !== "object") return properties;
        if (Array.isArray(properties)) return properties.map(sanitizeProperties);
        return Object.fromEntries(Object.entries(properties).flatMap(([key, value]) => {
            const normalized = key.toLowerCase();
            if (normalized.includes("referrer")) return [];
            if (normalized.includes("url") || normalized.includes("href") || normalized === "$pathname") {
                return [[key, template]];
            }
            if (["email", "name", "full_name"].includes(normalized)) return [];
            if (key === "$snapshot_data") return [[key, value]];
            return [[key, sanitizeProperties(value)]];
        }));
    };
    const sanitizedEvent = {
        ...event,
        properties: sanitizeProperties(event.properties),
    };
    if (event.event === "$pageview") {
        return {
            ...sanitizedEvent,
            properties: {
                ...sanitizedEvent.properties,
                $current_url: template,
                $pathname: template,
                route: template,
            },
        };
    }
    if (event.event === "$identify" || event.event === "$snapshot") return sanitizedEvent;
    return null;
}

async function getPostHog() {
    if (!posthogPromise) {
        posthogPromise = import("posthog-js/dist/posthog-recorder")
            .then(() => import("posthog-js/dist/module.no-external"))
            .then((module) => module.default);
    }
    try {
        posthogInstance = await posthogPromise;
        return posthogInstance;
    } catch (error) {
        posthogPromise = null;
        posthogInstance = null;
        throw error;
    }
}

async function initializeAnalytics(version) {
    const posthog = await getPostHog();
    if (version !== lifecycleVersion || !captureAllowed || !isReplayRoute(window.location.pathname)) return null;
    if (!initialized) {
        posthog.init(PROJECT_KEY, {
            api_host: API_HOST,
            defaults: "2025-11-30",
            persistence: "localStorage",
            person_profiles: "identified_only",
            autocapture: false,
            capture_pageview: false,
            capture_pageleave: false,
            capture_performance: false,
            capture_heatmaps: false,
            capture_dead_clicks: false,
            rageclick: false,
            capture_exceptions: false,
            disable_surveys: true,
            disable_surveys_automatic_display: true,
            disable_capture_url_hashes: true,
            save_referrer: false,
            save_campaign_params: false,
            disable_scroll_properties: true,
            advanced_disable_feature_flags: true,
            disable_session_recording: true,
            enable_recording_console_log: false,
            session_recording: {
                maskAllInputs: true,
                maskTextSelector: "[data-sensitive], .ph-mask",
                maskTextFn: () => "•••",
                maskAttributeFn: maskAttribute,
                blockSelector: [
                    "img", "picture", "video", "audio", "canvas", "iframe",
                    "[data-sensitive]", "[contenteditable='true']", "[class*='cl-']",
                    "[class*='clerk']", "[class*='stripe']", "[class*='payment']",
                ].join(","),
                recordHeaders: false,
                recordBody: false,
                captureCanvas: false,
                captureJsonLd: false,
                collectFonts: false,
                maskCapturedNetworkRequestFn: maskReplayRequest,
            },
            before_send: beforeSend,
        });
        initialized = true;
    } else {
        posthog.opt_in_capturing();
    }
    return posthog;
}

function revokeAnalytics() {
    captureAllowed = false;
    lifecycleVersion += 1;
    lastPageviewKey = null;
    selectedIdentity = undefined;
    if (!posthogInstance || !initialized) return;
    posthogInstance.stopSessionRecording();
    posthogInstance.reset(true);
    posthogInstance.opt_out_capturing();
}

function privacySignalEnabled() {
    return navigator.globalPrivacyControl === true
        || navigator.doNotTrack === "1"
        || window.doNotTrack === "1";
}

function readConsent() {
    try {
        const value = window.localStorage.getItem(CONSENT_KEY);
        return { choice: value === "accepted" || value === "declined" ? value : "undecided", error: false };
    } catch {
        return { choice: "undecided", error: true };
    }
}

export function AnalyticsProvider({ children }) {
    const location = useLocation();
    const { isSignedIn, isLoaded } = useAuth();
    const user = useUser();
    const initial = React.useState(readConsent)[0];
    const [choice, setChoice] = React.useState(initial.choice);
    const [storageError, setStorageError] = React.useState(initial.error);
    const [analyticsError, setAnalyticsError] = React.useState(false);
    const [startAttempt, setStartAttempt] = React.useState(0);
    const privacySignal = privacySignalEnabled();
    const consentAllowed = IS_AVAILABLE && choice === "accepted" && !storageError && !privacySignal;
    const authResolved = !USE_CLERK || isLoaded;
    const identifiedUserId = USE_CLERK && isLoaded && isSignedIn && user?.id ? user.id : null;

    const saveChoice = (nextChoice) => {
        if (nextChoice === "accepted" && privacySignal) return;
        if (nextChoice !== "accepted") revokeAnalytics();
        try {
            window.localStorage.setItem(CONSENT_KEY, nextChoice);
            setStorageError(false);
            setAnalyticsError(false);
            setChoice(nextChoice);
            if (nextChoice === "accepted") setStartAttempt((attempt) => attempt + 1);
        } catch {
            revokeAnalytics();
            setStorageError(true);
            setAnalyticsError(false);
            setChoice("undecided");
        }
    };

    React.useEffect(() => {
        const onStorage = (event) => {
            if (event.key !== CONSENT_KEY && event.key !== null) return;
            const nextChoice = event.key === CONSENT_KEY && (event.newValue === "accepted" || event.newValue === "declined")
                ? event.newValue
                : "undecided";
            if (nextChoice !== "accepted") revokeAnalytics();
            setChoice(nextChoice);
            setStorageError(false);
            setAnalyticsError(false);
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    React.useLayoutEffect(() => {
        const routeAllowed = isReplayRoute(location.pathname);
        const canCapture = consentAllowed && authResolved && routeAllowed;
        captureAllowed = canCapture;
        const version = ++lifecycleVersion;

        if (posthogInstance && initialized) {
            if ((!consentAllowed || !authResolved) && selectedIdentity !== undefined) {
                posthogInstance.stopSessionRecording();
                posthogInstance.reset(true);
                selectedIdentity = undefined;
                lastPageviewKey = null;
            } else if (
                consentAllowed
                && authResolved
                && selectedIdentity !== identifiedUserId
            ) {
                posthogInstance.stopSessionRecording();
                posthogInstance.reset(true);
                selectedIdentity = identifiedUserId;
                lastPageviewKey = null;
                if (identifiedUserId) posthogInstance.identify(identifiedUserId);
            }
        }

        if (!canCapture) {
            if (posthogInstance) posthogInstance.stopSessionRecording();
            return () => {
                if (version !== lifecycleVersion) return;
                captureAllowed = false;
                lifecycleVersion += 1;
                if (posthogInstance) posthogInstance.stopSessionRecording();
            };
        }

        const start = async () => {
            try {
                const posthog = await initializeAnalytics(version);
                if (!posthog || version !== lifecycleVersion || !captureAllowed) return;

                if (selectedIdentity !== identifiedUserId) {
                    posthog.stopSessionRecording();
                    posthog.reset(true);
                    selectedIdentity = identifiedUserId;
                    lastPageviewKey = null;
                    if (identifiedUserId) posthog.identify(identifiedUserId);
                }

                if (version !== lifecycleVersion || !captureAllowed) return;
                if (lastPageviewKey !== location.key) {
                    const template = routeTemplate(location.pathname);
                    posthog.capture("$pageview", { route: template, $pathname: template, $current_url: template });
                    lastPageviewKey = location.key;
                }
                if (version === lifecycleVersion && captureAllowed) posthog.startSessionRecording();
                setAnalyticsError(false);
            } catch (error) {
                if (version !== lifecycleVersion) return;
                captureAllowed = false;
                if (posthogInstance) posthogInstance.stopSessionRecording();
                console.error("PostHog analytics failed to initialize.", error);
                setAnalyticsError(true);
            }
        };
        void start();
        return () => {
            if (version !== lifecycleVersion) return;
            captureAllowed = false;
            lifecycleVersion += 1;
            if (posthogInstance) posthogInstance.stopSessionRecording();
        };
    }, [
        authResolved,
        consentAllowed,
        identifiedUserId,
        location.key,
        location.pathname,
        startAttempt,
    ]);

    const value = {
        available: IS_AVAILABLE,
        choice,
        storageError,
        analyticsError,
        privacySignal,
        accept: () => saveChoice("accepted"),
        decline: () => saveChoice("declined"),
    };

    return (
        <AnalyticsConsentContext.Provider value={value}>
            {children}
        </AnalyticsConsentContext.Provider>
    );
}

export function useAnalyticsConsent() {
    const context = React.useContext(AnalyticsConsentContext);
    if (!context) throw new Error("useAnalyticsConsent must be used inside AnalyticsProvider");
    return context;
}
